import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import SimpleReactValidator from 'simple-react-validator';

import { FilePond, registerPlugin } from 'react-filepond';

import 'filepond/dist/filepond.min.css';

import services from '@C/ManageBunk/RouteComponents/OpeningBalanceSetup/data';
import { formatCurrency, TableGrid } from '@C/TableGrid';
import { AccessControl, DateFilter } from '@C/Utils';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { authHeader } from '@H';
import * as XLSX from 'xlsx';
import { authService } from '@S';

import './styles.scoped.scss';

const CreditCustomersBunkSetup = () => {
  const [state, setState] = useState({
    loading: false,
    updated: false,
    customer: [
      {
        first_name: '',
        last_name: '',
        password: '',
        is_password_update: false,
        mobile: '',
        email: '',
        gstn: '',
        pan: '',
        user_role: 104,
        credit_customer: {
          vehicle: [
            {
              vehicle_no: '',
              driver_name: '',
              mobile: '',
            },
          ],
          billing_cycle: {
            start_date: '',
            payment_period: '',
            limit: '',
            balance: '',
            outstanding: '',
          },
          address: {
            country: '',
            state: '',
            city: '',
            pincode: null,
            country_code: '',
            full_address: '',
          },
        },
      },
    ],
    notice: '',
  });
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const myRef = useRef(null);
  const [, forceUpdate] = useState();
  const simpleValidator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } }),
  );

  const addRow = () => {
    setState((prevState) => ({
      ...prevState,
      customer: [
        ...state.customer,
        {
          first_name: '',
          last_name: '',
          password: '',
          is_password_update: false,
          mobile: '',
          email: '',
          gstn: '',
          pan: '',
          user_role: 104,
          credit_customer: {
            vehicle: [
              {
                vehicle_no: '',
                driver_name: '',
                mobile: '',
              },
            ],
            billing_cycle: {
              start_date: '',
              payment_period: '',
              limit: '',
              balance: '',
              outstanding: '',
            },
            address: {
              country: '',
              state: '',
              city: '',
              pincode: null,
              country_code: '',
              full_address: '',
            },
          },
        },
      ],
    }));
  };

  useEffect(() => {
    simpleValidator.current.showMessageFor('Date');
  }, []);

  const deleteRow = (index) => {
    const list = [...state.customer];
    list.splice(index, 1);
    setState((prevState) => ({
      ...prevState,
      customer: list,
    }));
  };

  const handleChange = (e, index) => {
    const { name, value } = e.target;
    const updatedCustomer = [...state.customer];
    updatedCustomer[index][name] = value;

    // Recalculate duplicate flags
    const mobileCount = {};
    updatedCustomer.forEach((item) => {
      const mobile = item.mobile;
      if (mobile) {
        mobileCount[mobile] = (mobileCount[mobile] || 0) + 1;
      }
    });

    updatedCustomer.forEach((item) => {
      item.has_duplicate = mobileCount[item.mobile] > 1;
    });

    setState((prevState) => ({
      ...prevState,
      customer: updatedCustomer,
    }));

    // Trigger re-validation
    setTimeout(() => forceUpdate((prev) => !prev), 0);
  };

  const handleBillingCycleData = (e, index) => {
    const { name, value } = e.target;
    const list = [...state.customer];
    list[index].credit_customer.billing_cycle[name] = value;
    setState((prevState) => ({
      ...prevState,
      customer: list,
    }));
  };

  const importGoogleSheet = async () => {
    if (!googleSheetUrl) {
      alert('Please enter a valid Google Sheet URL.');
      return;
    }

    try {
      // Extract the sheet ID from the Google Sheet URL
      const sheetId = googleSheetUrl.match(/\/d\/(.*?)\//)?.[1];
      if (!sheetId) {
        alert('Invalid Google Sheet URL.');
        return;
      }

      // Fetch the sheet data as CSV
      const response = await axios.get(
        `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv`,
      );

      // Parse the CSV data into JSON
      const workbook = XLSX.read(response.data, { type: 'string' });
      const sheetName = workbook.SheetNames[0];
      const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

      // Process the data (similar to your existing importExcel logic)
      const formattedRows = sheetData.map((item) => ({
        first_name: item['First Name'] || '',
        last_name: item['Last Name'] || '',
        mobile: item['Mobile number'] || '',
        email: item['Email'] || '',
        user_role: 104,
        credit_customer: {
          vehicle: [
            {
              vehicle_no: '',
              driver_name: '',
              mobile: '',
            },
          ],
          billing_cycle: {
            start_date: XLSX.SSF.format('YYYY-MM-DD', item['Date']),
            payment_period: item['Billing cycle (in days)'] || '',
            limit: item['Limit'] || '',
            outstanding: item['Opening balance'] || '',
          },
          address: {
            country: '',
            state: '',
            city: '',
            pincode: null,
            full_address: '',
          },
        },
      }));

      setState((prevState) => ({
        ...prevState,
        customer: formattedRows,
      }));
    } catch (error) {
      console.error('Error importing Google Sheet:', error);
      alert(
        'Failed to import Google Sheet. Please check the URL and try again.',
      );
    }
  };

  const importExcel = (fileItem) => {
    const file = fileItem.file;

    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const wb = XLSX.read(event.target.result, { type: 'array' });
        const sheets = wb.SheetNames;

        if (sheets.length) {
          const productsRows = XLSX.utils.sheet_to_json(wb.Sheets[sheets[0]]);
          const formattedRows = productsRows.map((item) => {
            return Object.keys(item).reduce((acc, key) => {
              const formattedKey = key.replace(/\s+/g, '_').toLowerCase();
              const formattedKeyWithoutBrackets = formattedKey.replace(
                /\(.*\)/,
                '',
              );
              acc[formattedKeyWithoutBrackets] = item[key];
              return acc;
            }, {});
          });

          // Create a frequency map for mobile numbers
          const mobileCount = {};
          formattedRows.forEach((item) => {
            const mobile = item.mobile_number;
            if (mobile) {
              mobileCount[mobile] = (mobileCount[mobile] || 0) + 1;
            }
          });

          // Build customer data with duplicate flag
          const customerData = formattedRows.map((item) => {
            const isDuplicate = mobileCount[item.mobile_number] > 1;
            return {
              first_name: item.first_name || '',
              last_name: item.last_name || '',
              password: '',
              mobile: item.mobile_number || '',
              email: item.email || '',
              has_duplicate: isDuplicate,
              gstn: '',
              pan: '',
              user_role: 104,
              credit_customer: {
                vehicle: [
                  {
                    vehicle_no: '',
                    driver_name: '',
                    mobile: '',
                  },
                ],
                billing_cycle: {
                  start_date: XLSX.SSF.format('YYYY-MM-DD', item.date),
                  payment_period: item.billing_cycle_,
                  limit: item.limit,
                  balance: '',
                  outstanding: item.opening_balance,
                },
                address: {
                  country: '',
                  state: '',
                  city: '',
                  pincode: null,
                  full_address: '',
                },
              },
            };
          });

          setState((prevState) => ({
            ...prevState,
            customer: customerData,
          }));

          // Force validation to render errors
          setTimeout(() => forceUpdate((prev) => !prev), 0);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate((prev) => !prev); // Ensure re-render
      return;
    }

    setIsSubmitting(true);
    let formData = {
      user_list: state.customer,
    };

    const headers = authHeader(authService.currentAccountID);
    let url = `${process.env.API_URL}/bulk-users/register/`;

    axios
      .post(url, formData, { headers })
      .then((response) => {
        if (response.data.status === 201) {
          toast.success(`Credit customers added successfully`);
          window.scrollTo({
            behavior: 'smooth',
            top: myRef.current.offsetTop - 150,
          });

          setIsSubmitting(false);
          setState({
            customer: [
              {
                first_name: '',
                last_name: '',
                password: '',
                is_password_update: false,
                mobile: '',
                email: '',
                gstn: '',
                pan: '',
                user_role: 104,
                credit_customer: {
                  vehicle: [
                    {
                      vehicle_no: '',
                      driver_name: '',
                      mobile: '',
                    },
                  ],
                  billing_cycle: {
                    start_date: '',
                    payment_period: '',
                    limit: '',
                    balance: '',
                    outstanding: '',
                  },
                  address: {
                    country: '',
                    state: '',
                    city: '',
                    pincode: null,
                    country_code: '',
                    full_address: '',
                  },
                },
              },
            ],
          });

          simpleValidator.current.hideMessages();
          forceUpdate((prev) => !prev);
        } else if (response.data.status === 226) {
          setIsSubmitting(false);
          const errorNumbers = response.data.data.map(Number);
          const notice = `The below customers are already registered as users in a different bunk<br/>
          Please navigate to the <a target="_blank" href="/account/credit-customers/list">Credit Customer module</a> to add these customers`;
          setState((prevState) => {
            const updatedCustomers = [...prevState.customer];

            const matchingObjects = updatedCustomers.filter((obj) =>
              errorNumbers.includes(parseInt(obj.mobile)),
            );
            // if the response.data.message string contains the phrase 'Partially created', then display a toast message
            if (response.data.message.includes('Partially created')) {
              toast.success(
                <div
                  dangerouslySetInnerHTML={{
                    __html: `Some customers were added successfully. <br/><br/>Please check the message above for details on the customers that were not added.`,
                  }}
                />,
              );
            }
            return {
              ...prevState,
              customer: matchingObjects,
              notice,
            };
          });
        } else if (response.data.status === 208) {
          setIsSubmitting(false);
          toast.error(response.data.message);
        } else {
          setIsSubmitting(false);
          toast.success('Customer added successfuly');
          setState({
            customer: [
              {
                first_name: '',
                last_name: '',
                password: '',
                is_password_update: false,
                mobile: '',
                email: '',
                gstn: '',
                pan: '',
                user_role: 104,
                credit_customer: {
                  vehicle: [
                    {
                      vehicle_no: '',
                      driver_name: '',
                      mobile: '',
                    },
                  ],
                  billing_cycle: {
                    start_date: '',
                    payment_period: '',
                    limit: '',
                    balance: '',
                    outstanding: '',
                  },
                  address: {
                    country: '',
                    state: '',
                    city: '',
                    pincode: null,
                    country_code: '',
                    full_address: '',
                  },
                },
              },
            ],
          });
          forceUpdate((prev) => !prev); // Force re-render to show errors
        }
      })
      .catch((error) => {
        setIsSubmitting(false);
        toast.error(error.response?.data?.message);
      });
  };

  return (
    <AccessControl allowedRoles={['Dealer']}>
      {state.notice && (
        <div className="notification-container" style={{ width: '100%' }}>
          <div
            className="notification-alert notification-alert__error"
            style={{ marginBottom: '0px' }}
          >
            <div dangerouslySetInnerHTML={{ __html: state.notice }} />
          </div>
        </div>
      )}
      <section className="widget full-width form-table" ref={myRef}>
        <header className="form-header">
          <h3>Add Credit Customers</h3>
          <div style={{ marginTop: '20px' }}>
            <div className="input-wrapper  with-label with-btn">
              <label>Google Sheet URL</label>
              <input
                type="text"
                id="googleSheetUrl"
                value={googleSheetUrl}
                onChange={(e) => setGoogleSheetUrl(e.target.value)}
              />
              <button onClick={importGoogleSheet} className="blue-btn">
                Import
              </button>
            </div>
          </div>
          <div className="actions" style={{ width: '40rem' }}>
            <div className="vn-red" style={{ textAlign: 'center' }}>
              <a
                href="https://petrometerproductions3.s3.ap-south-1.amazonaws.com/media/Credit+Customers.xlsx"
                target="_blank"
              >
                Download excel template
                <i className="fa fa-cloud-download  fa-lg"></i>
              </a>
            </div>
            <div
              className={`input-wrapper file-upload `}
              style={{ width: '100%' }}
            >
              <FilePond
                credits={false}
                files={files}
                onupdatefiles={(fileItems) => {
                  setFiles(fileItems);
                  if (fileItems.length > 0) {
                    importExcel(fileItems[0]); // Pass the selected file to importExcel
                  }
                }}
                allowMultiple={false}
                name="files"
                labelIdle='Drag & Drop your files or <span className="filepond--label-action">Browse</span>'
                acceptedFileTypes={[
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  'application/vnd.ms-excel',
                ]}
              />
            </div>

            <button
              className={`add-row`}
              onClick={() => addRow()}
              style={{ width: '50px' }}
            >
              Add row
            </button>
          </div>
        </header>
        <main>
          <div className="form-content">
            <table>
              <thead>
                <tr>
                  <th> Date </th>
                  <th>First name</th>
                  <th>Last name</th>
                  <th>Mobile</th>
                  <th>Billing cycle</th>
                  <th>Limit</th>
                  <th>Opening balance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.customer.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <div className="input-wrapper">
                        {simpleValidator.current.message(
                          `date_${index}`,
                          item.credit_customer.billing_cycle.start_date,
                          'required',
                        )}
                        <DateFilter
                          name="start_date"
                          onChange={(e) => handleBillingCycleData(e, index)}
                          value={item.credit_customer.billing_cycle.start_date}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="input-wrapper">
                        {simpleValidator.current.message(
                          `first_name_${index}`,
                          item.first_name,
                          'required|alpha_num_space|max:30,string',
                        )}
                        <input
                          type="text"
                          name="first_name"
                          value={item.first_name}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="input-wrapper">
                        <input
                          type="text"
                          name="last_name"
                          value={item.last_name}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="input-wrapper">
                        {simpleValidator.current.message(
                          `mobile_${index}`,
                          item.mobile,
                          item.has_duplicate
                            ? 'required|regex:^\\d{10}$|in:__DUPLICATE__'
                            : 'required|regex:^\\d{10}$',
                        )}
                        <input
                          type="text"
                          name="mobile"
                          value={item.mobile}
                          onChange={(e) => handleChange(e, index)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="input-wrapper">
                        {simpleValidator.current.message(
                          `billing_cycle_${index}`,
                          item.credit_customer.billing_cycle.payment_period,
                          'required|numeric|min:1,num',
                        )}
                        <input
                          type="text"
                          name="payment_period"
                          value={
                            item.credit_customer.billing_cycle.payment_period
                          }
                          onChange={(e) => handleBillingCycleData(e, index)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="input-wrapper">
                        {simpleValidator.current.message(
                          `limit_${index}`,
                          item.credit_customer.billing_cycle.limit,
                          'required|numeric|min:0,num',
                        )}
                        <input
                          type="text"
                          name="limit"
                          value={item.credit_customer.billing_cycle.limit}
                          onChange={(e) => handleBillingCycleData(e, index)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="input-wrapper">
                        {simpleValidator.current.message(
                          `opening_balance_${index}`,
                          item.credit_customer.billing_cycle.outstanding,
                          'required|numeric',
                        )}
                        <input
                          type="text"
                          name="outstanding"
                          value={item.credit_customer.billing_cycle.outstanding}
                          onChange={(e) => handleBillingCycleData(e, index)}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="input-wrapper">
                        <button
                          className="delete-row"
                          onClick={(e) => deleteRow(index)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="form-actions">
            <button
              className={`submit ${isSubmitting ? 'loading' : ''}`}
              type="submit"
              disabled={state.isSubmitting}
              onClick={submitForm}
            >
              Submit
            </button>
          </div>
        </main>
      </section>
    </AccessControl>
  );
};
export { CreditCustomersBunkSetup };
